import { createPersistedStore, type PersistedStoreHook } from '@tasama/utils';
import type { BaseStoreState } from '@tasama/utils';
import type { ProjectDetailsFormValues } from '../schemas/projectDetails.schema';

/**
 * Project Details coordinator store — thin coordinator (portal)
 *
 * No page component exists yet for portal project details.
 * When a page is built it should use react-hook-form + zodResolver
 * (projectDetailsSchema) and sync isValid + formData here, following
 * the same thin-coordinator pattern as the onboarding stores.
 *
 * Removed: draftValues, errors, isDirty, submitHandler, interactedFields,
 * setDraftValues, setFieldError, clearErrors, markFieldAsInteracted,
 * validateField, validateForm, setSubmitHandler.
 */

interface ProjectDetailsState extends BaseStoreState {
  formData: ProjectDetailsFormValues;
  isValid: boolean;
}

export interface ProjectDetailsStore extends ProjectDetailsState {
  setValidity: (_valid: boolean) => void;
  setFormData: (_values: Partial<ProjectDetailsFormValues>) => void;
  updateFormData: (_data: ProjectDetailsFormValues) => void;
  reset: () => void;
}

const initialFormData: ProjectDetailsFormValues = {
  projectCodeName: '',
  projectDetails: '',
  expectedLaunchDate: '',
};

export const useProjectDetailsStore = createPersistedStore<ProjectDetailsState>(
  'project-details',
  {
    formData: initialFormData,
    isValid: false,
  },
  (set, get) => ({
    setValidity: (isValid: boolean) => set({ isValid }),

    setFormData: (values: Partial<ProjectDetailsFormValues>) => {
      const { formData } = get();
      set({ formData: { ...formData, ...values } as ProjectDetailsFormValues });
    },

    updateFormData: (data: ProjectDetailsFormValues) => {
      set({ formData: data });
    },

    reset: () => set({ formData: initialFormData, isValid: false }),
  }),
  {
    partialize: (state) => ({
      formData: state.formData,
      isValid: state.isValid,
    }),
  }
) as unknown as PersistedStoreHook<ProjectDetailsStore>;

export const useProjectDetailsFormData = () =>
  useProjectDetailsStore((state) => state.formData);
export const useProjectDetailsValues = () =>
  useProjectDetailsStore((state) => state.formData);
export const useProjectDetailsIsValid = () =>
  useProjectDetailsStore((state) => state.isValid);
// Stub kept for backward-compatibility
export const useProjectDetailsErrors = () =>
  ({}) as Partial<Record<keyof ProjectDetailsFormValues, string>>;
